import { Constants } from '../constants';
import { SavedLoadoutRecord, SharedLoadoutImportCandidate, buildSavedLoadoutRecord, normalizeSavedLoadoutRecord } from './loadout-sharing';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

interface SavedLoadoutsStateDocument {
    records?: SavedLoadoutRecord[];
}

export function loadSavedLoadouts(storage: StorageLike = localStorage): SavedLoadoutRecord[] {
    const parsed = parseStoredJson<SavedLoadoutsStateDocument>(storage.getItem(Constants.LocalStorageKeys.SAVED_LOADOUTS_STATE));
    if (!parsed || !Array.isArray(parsed.records)) {
        return [];
    }

    const normalizedRecords = parsed.records
        .map((record) => normalizeSavedLoadoutRecord(record))
        .filter((record): record is SavedLoadoutRecord => Boolean(record));

    return normalizedRecords.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function saveSavedLoadouts(records: SavedLoadoutRecord[], storage: StorageLike = localStorage) {
    if (records.length === 0) {
        storage.removeItem(Constants.LocalStorageKeys.SAVED_LOADOUTS_STATE);
        return;
    }

    storage.setItem(
        Constants.LocalStorageKeys.SAVED_LOADOUTS_STATE,
        JSON.stringify({
            records: records
                .map((record) => normalizeSavedLoadoutRecord(record))
                .filter((record): record is SavedLoadoutRecord => Boolean(record))
                .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
        }),
    );
}

export function isFavoriteSavedLoadout(records: SavedLoadoutRecord[], candidate: SharedLoadoutImportCandidate): boolean {
    const recordId = buildSavedLoadoutRecord(candidate).id;
    return records.some((record) => record.id === recordId && record.isFavorite);
}

export function toggleFavoriteSavedLoadout(
    records: SavedLoadoutRecord[],
    candidate: SharedLoadoutImportCandidate,
    now: string = new Date().toISOString(),
): SavedLoadoutRecord[] {
    const nextRecord = buildSavedLoadoutRecord(candidate, {
        createdAt: now,
        updatedAt: now,
        isFavorite: true,
    });
    const existingRecord = records.find((record) => record.id === nextRecord.id);

    if (existingRecord?.isFavorite) {
        return records.filter((record) => record.id !== nextRecord.id);
    }

    return [
        {
            ...nextRecord,
            createdAt: existingRecord?.createdAt ?? nextRecord.createdAt,
        },
        ...records.filter((record) => record.id !== nextRecord.id),
    ].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function removeSavedLoadout(records: SavedLoadoutRecord[], recordId: string): SavedLoadoutRecord[] {
    return records.filter((record) => record.id !== recordId);
}

function parseStoredJson<T>(value: string | null): T | null {
    if (!value) {
        return null;
    }

    try {
        return JSON.parse(value) as T;
    } catch {
        return null;
    }
}
